import {
  AudienceDto,
  CreateAudienceDto,
} from "@intelligems/ig-types/dtos/audiences";
import { CartTargetingDto } from "@intelligems/ig-types/dtos/cart-targeting";
import { CheckoutBlockDto } from "@intelligems/ig-types/dtos/checkout-blocks";
import { ComponentDto } from "@intelligems/ig-types/dtos/components";
import { CreateExclusionGroupDto } from "@intelligems/ig-types/dtos/exclusion-group";
import { CreateExperienceCustomMetricDto } from "@intelligems/ig-types/dtos/experience-custom-metrics";
import { CreateExperienceKeyMetricDto } from "@intelligems/ig-types/dtos/experience-key-metrics";
import { CreateExperienceProductTargetingDto } from "@intelligems/ig-types/dtos/experience-product-targeting";
import {
  CreateOnsiteInjectionDto,
  OnsiteInjectionDto,
} from "@intelligems/ig-types/dtos/onsite-injections";
import { ExperienceFe } from "@intelligems/ig-types/fe-only/experiences";
import { ExperiencePageTargetingFe } from "@intelligems/ig-types/fe-only/page-targeting";
import { OfferDto } from "@intelligems/ig-types/dtos/offers";
import { OnsiteEditDto } from "@intelligems/ig-types/dtos/onsite-edits";
import { PutExperienceRequestBodyType } from "@intelligems/ig-types/functions/experiences/put-experience";
import { RedirectDto } from "@intelligems/ig-types/dtos/redirects";
import { UpsellProductRulesDto } from "@intelligems/ig-types/dtos/upsell-product-rules";
import { UserInterfaceDto } from "@intelligems/ig-types/dtos/user-interfaces";
import { VariationFe } from "@intelligems/ig-types/fe-only/variations";
import { createSelector } from "@reduxjs/toolkit";
import { formatAudienceDto } from "@src/api/formatters/from-dto/audience";
import { getExperienceDtoFromFe } from "@src/api/formatters/from-dto/experiences";
import { getVariationDtoFromFe } from "@src/api/formatters/from-dto/variations";
import type { RootState } from "@src/store/rootState";
import {
  getCartTargetingForPayload,
  getStoredCartTargetingForPayload,
} from "@src/store/slices/cartTargeting/selectors";
import {
  selectedExclusionGroup,
  selectedStoredExclusionGroup,
} from "@src/store/slices/exclusionGroups";
import { getExperienceProductTargetingForPayload } from "@src/store/slices/experienceProductTargeting/selectors";
import { getUserInterfacesPutPayload } from "@src/store/slices/experiences/util";
import {
  getStoredUpsellProductRulesForPayload,
  getUpsellProductRulesForPayload,
} from "@src/store/slices/upsellProductRules/selectors";

export const getPutExperimentPayload = createSelector(
  (state: RootState) => state.experience.experience as ExperienceFe,
  (state: RootState) => state.variations.variations,
  (state: RootState) => state.onsiteInjections.injections,
  (state: RootState) => state.onsiteEdits.allEdits,
  (state: RootState) => state.offers.offers,
  (state: RootState) => state.redirects.redirects,
  (state: RootState) => state.audience.audience as AudienceDto,
  (state: RootState) => state.pageTargeting.pageTargeting,
  selectedExclusionGroup,
  (state: RootState) => state.userInterfaces.interfaces,
  (state: RootState) => state.experienceMetrics.keyMetrics,
  (state: RootState) => state.experienceMetrics.customMetrics,
  (state: RootState) => state.checkoutBlocks.checkoutBlocks,
  (state: RootState) => state.checkoutBlocks.postPurchaseBlocks,
  getExperienceProductTargetingForPayload,
  getCartTargetingForPayload,
  getUpsellProductRulesForPayload,
  (state: RootState) => state.experienceComponents.components,
  (
    experience,
    variations,
    onsiteInjections,
    onsiteEdits,
    offers,
    redirects,
    audience,
    pageTargeting,
    exclusionGroup,
    userInterfaces,
    experienceKeyMetrics,
    experienceCustomMetrics,
    checkoutBlocks,
    postPurchaseBlocks,
    experienceProductTargeting,
    cartTargeting,
    upsellProductRules,
    components
  ) => {
    return createPayload({
      experience,
      variations,
      onsiteInjections,
      onsiteEdits,
      offers,
      redirects,
      audience,
      pageTargeting,
      exclusionGroup: exclusionGroup as CreateExclusionGroupDto | undefined,
      userInterfaces,
      experienceKeyMetrics,
      experienceCustomMetrics,
      checkoutBlocks: [...checkoutBlocks, ...postPurchaseBlocks],
      experienceProductTargeting,
      cartTargeting,
      upsellProductRules,
      components,
    });
  }
);

export const getPutStoredExperimentPayload = createSelector(
  (state: RootState) => state.experience.storedExperience as ExperienceFe,
  (state: RootState) => state.variations.storedVariations,
  (state: RootState) => state.onsiteInjections.storedInjections,
  (state: RootState) => state.onsiteEdits.storedEdits,
  (state: RootState) => state.offers.storedOffers,
  (state: RootState) => state.redirects.storedRedirects,
  (state: RootState) => state.audience.storedAudience as AudienceDto,
  (state: RootState) => state.pageTargeting.storedPageTargeting,
  selectedStoredExclusionGroup,
  (state: RootState) => state.userInterfaces.storedInterfaces,
  (state: RootState) => state.experienceMetrics.storedKeyMetrics,
  (state: RootState) => state.experienceMetrics.storedCustomMetrics,
  (state: RootState) => state.checkoutBlocks.storedCheckoutBlocks,
  (state: RootState) => state.checkoutBlocks.storedPostPurchaseBlocks,
  (state: RootState) =>
    state.experienceProductTargeting.storedExperienceProductTargeting,
  getStoredCartTargetingForPayload,
  getStoredUpsellProductRulesForPayload,
  (state: RootState) => state.experienceComponents.storedComponents,
  (
    experience,
    variations,
    onsiteInjections,
    onsiteEdits,
    offers,
    redirects,
    audience,
    pageTargeting,
    exclusionGroup,
    userInterfaces,
    experienceKeyMetrics,
    experienceCustomMetrics,
    checkoutBlocks,
    storedPostPurchaseBlocks,
    experienceProductTargeting,
    cartTargeting,
    upsellProductRules,
    components
  ) => {
    return createPayload({
      experience,
      variations,
      onsiteInjections,
      onsiteEdits,
      offers,
      redirects,
      audience,
      pageTargeting,
      exclusionGroup: exclusionGroup,
      userInterfaces,
      experienceKeyMetrics,
      experienceCustomMetrics,
      checkoutBlocks: [...checkoutBlocks, ...storedPostPurchaseBlocks],
      experienceProductTargeting,
      cartTargeting,
      upsellProductRules,
      components,
    });
  }
);

function createPayload({
  experience,
  variations,
  onsiteInjections,
  onsiteEdits,
  offers,
  redirects,
  audience,
  pageTargeting,
  exclusionGroup,
  userInterfaces,
  experienceKeyMetrics,
  experienceCustomMetrics,
  checkoutBlocks,
  experienceProductTargeting,
  cartTargeting,
  upsellProductRules,
  components,
}: {
  experience: ExperienceFe;
  variations: VariationFe[];
  onsiteInjections: OnsiteInjectionDto[] | CreateOnsiteInjectionDto[];
  onsiteEdits: OnsiteEditDto[];
  offers: OfferDto[];
  redirects: RedirectDto[];
  audience: AudienceDto;
  pageTargeting: ExperiencePageTargetingFe[];
  userInterfaces: UserInterfaceDto[] | undefined;
  experienceKeyMetrics: CreateExperienceKeyMetricDto[];
  experienceCustomMetrics: CreateExperienceCustomMetricDto[];
  exclusionGroup?: CreateExclusionGroupDto;
  checkoutBlocks: CheckoutBlockDto[];
  experienceProductTargeting: CreateExperienceProductTargetingDto[];
  cartTargeting: CartTargetingDto[];
  upsellProductRules: UpsellProductRulesDto[];
  components: ComponentDto[];
}): PutExperienceRequestBodyType | undefined {
  if (!experience) {
    return undefined;
  }

  try {
    return {
      experience: getExperienceDtoFromFe(experience, checkoutBlocks),
      variations: getVariationDtoFromFe(variations),
      onsiteEdits,
      onsiteInjections,
      offers,
      redirects,
      experiencePageTargeting:
        redirects.filter((redirect) => !redirect.isTemplateTest).length > 0
          ? []
          : pageTargeting,
      exclusionGroup,
      audience: formatAudienceDto(audience) as CreateAudienceDto,
      userInterfaces: getUserInterfacesPutPayload(userInterfaces),
      experienceKeyMetrics,
      experienceCustomMetrics,
      checkoutBlocks,
      experienceProductTargeting,
      cartTargeting,
      upsellProductRules,
      components,
    };
  } catch {
    return undefined;
  }
}
